import i18n from '@/utils/i18n';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { createStyles } from '@/contexts/theme-context';
import {
  DRAG_HANDLE_RESERVE,
  ITEM_HEADER_ROW_HEIGHT,
  PAR_COLUMN_WIDTH,
} from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/order-guides-list-constants';
import { View, ViewStyle } from 'react-native';

const SKELETON_ROW_COUNT = 8;
const SKELETON_PILL_COUNT = 4;

const ITEM_NAME_WIDTH = 160;
const ITEM_NAME_HEIGHT = 14;
const PACK_DETAILS_WIDTH = 96;
const PACK_DETAILS_HEIGHT = 10;
const PAR_INPUT_HEIGHT = 32;
const COLUMN_LABEL_WIDTH = 40;
const COLUMN_LABEL_HEIGHT = 12;
const PILL_HEIGHT = 32;
const CHECKBOX_SIZE = 16;
const PILL_WIDTH = 80;
const SEARCH_BAR_HEIGHT = 40;
const SEARCH_BAR_RADIUS = 8;

const ROW_FADE_STEP = 0.1;
const PILL_FADE_STEP = 0.25;

const getFadeStyle = (index: number, step: number): ViewStyle => ({
  opacity: 1 - index * step,
});

/**
 * Loading placeholder for the items area, mirroring the group pills, column
 * header, and `ItemRow`'s layout — pills fade out towards the right, rows
 * towards the bottom.
 */
export const OrderGuideItemsSkeleton: React.FC = () => {
  const [styles, theme] = useStyles();

  return (
    <View
      accessibilityLabel={i18n.t('order_guide')}
      accessibilityRole="progressbar"
      accessibilityState={{ busy: true }}
      style={styles.container}
    >
      <AnimatedSkeletonLoader
        borderRadius={SEARCH_BAR_RADIUS}
        height={SEARCH_BAR_HEIGHT}
      />
      <View style={styles.pillRow}>
        {Array.from({ length: SKELETON_PILL_COUNT }).map((_, pillIndex) => (
          <View key={pillIndex} style={getFadeStyle(pillIndex, PILL_FADE_STEP)}>
            <AnimatedSkeletonLoader
              borderRadius={theme.sk.radii.radiusRound}
              height={PILL_HEIGHT}
              width={PILL_WIDTH}
            />
          </View>
        ))}
      </View>
      <View>
        <View style={styles.columnHeader}>
          <View style={styles.checkboxRow}>
            <AnimatedSkeletonLoader
              borderRadius={theme.sk.radii.radiusSmall}
              height={CHECKBOX_SIZE}
              width={CHECKBOX_SIZE}
            />
            <AnimatedSkeletonLoader
              borderRadius={theme.sk.radii.radiusSmall}
              height={COLUMN_LABEL_HEIGHT}
              width={COLUMN_LABEL_WIDTH}
            />
          </View>
          <View style={styles.parColumn}>
            <AnimatedSkeletonLoader
              borderRadius={theme.sk.radii.radiusSmall}
              height={COLUMN_LABEL_HEIGHT}
              width={COLUMN_LABEL_WIDTH}
            />
          </View>
        </View>
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
          <View
            key={rowIndex}
            style={[styles.row, getFadeStyle(rowIndex, ROW_FADE_STEP)]}
          >
            <View style={styles.identity}>
              <View style={styles.rowLeading}>
                <AnimatedSkeletonLoader
                  borderRadius={theme.sk.radii.radiusSmall}
                  height={CHECKBOX_SIZE}
                  width={CHECKBOX_SIZE}
                />
                <AnimatedSkeletonLoader
                  borderRadius={theme.sk.radii.radiusMedium}
                  height={theme.sk.spacing.space2xlarge}
                  width={theme.sk.spacing.space2xlarge}
                />
              </View>
              <View style={styles.text}>
                <AnimatedSkeletonLoader
                  borderRadius={theme.sk.radii.radiusSmall}
                  height={ITEM_NAME_HEIGHT}
                  width={ITEM_NAME_WIDTH}
                />
                <AnimatedSkeletonLoader
                  borderRadius={theme.sk.radii.radiusSmall}
                  height={PACK_DETAILS_HEIGHT}
                  width={PACK_DETAILS_WIDTH}
                />
              </View>
            </View>
            <AnimatedSkeletonLoader
              borderRadius={theme.sk.radii.radiusMedium}
              height={PAR_INPUT_HEIGHT}
              width={PAR_COLUMN_WIDTH}
            />
          </View>
        ))}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  checkboxRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    paddingLeft: theme.sk.spacing.spaceSmall,
  },
  columnHeader: {
    alignItems: 'center',
    borderBottomWidth: 1,
    borderColor: theme.sk.color.border.primary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingRight: theme.sk.spacing.spaceSmall + DRAG_HANDLE_RESERVE,
    paddingVertical: 10,
  },
  container: {
    gap: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  identity: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
  },
  parColumn: {
    alignItems: 'center',
    width: PAR_COLUMN_WIDTH,
  },
  pillRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  row: {
    alignItems: 'center',
    borderBottomWidth: 1,
    borderColor: theme.sk.color.border.primary,
    flexDirection: 'row',
    height: ITEM_HEADER_ROW_HEIGHT,
    justifyContent: 'space-between',
    paddingRight: theme.sk.spacing.spaceSmall + DRAG_HANDLE_RESERVE,
  },
  rowLeading: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium + theme.sk.spacing.spaceXsmall,
    paddingLeft: theme.sk.spacing.spaceSmall,
  },
  text: {
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
